import React, { useEffect, useRef, useState } from 'react';
import { Box, Grid, Alert, FormControlLabel, Checkbox, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import InvButton from '../../components/InvButton';
import { InvTextField, InvSelect } from '../../components/InvField';
import WoLineItemsEditor from '../../components/material/WoLineItemsEditor';
import RfqVendorSelector from '../../components/material/RfqVendorSelector';
import RfqAttachmentsPanel from '../../components/material/RfqAttachmentsPanel';
import { getWorkOrder, updateAndPublishWorkOrder } from '../../api/workOrder';
import { summarizeWoInviteNotify } from '../../utils/woInviteNotify';
import { getPurchasableVendors } from '../../api/vendor';
import { emptyWoLine, toWoCreatePayload, validateWoForm, fromApiWorkOrder } from '../../utils/woEntity';
import { WO_MODES, WO_TYPE_SUGGESTIONS } from '../../constants/workOrder';
import { IPD_COLORS } from '../../theme';
import useItemNames from '../../hooks/useItemNames';

const FORM_ID = 'wo-edit-form';

export default function WoEdit() {
  const { id } = useParams();
  const { url } = useRouteMatch();
  const history = useHistory();
  const detailUrl = url.replace(/\/edit$/, '');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [vendorError, setVendorError] = useState(null);
  const [lines, setLines] = useState([emptyWoLine()]);
  const [vendorIds, setVendorIds] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [vendors, setVendors] = useState([]);
  const submitModeRef = useRef('draft');
  const { getPurchaseUomCode, getBaseUomCode, getPurchaseUomId, getGstRate } = useItemNames();

  const { control, handleSubmit, watch, reset, setValue, formState: { errors } } = useForm({
    defaultValues: { mode: 'DIRECT', title: '', is_open_bid: true },
  });
  const mode = watch('mode');

  useEffect(() => {
    getPurchasableVendors().then(setVendors).catch(() => setVendors([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const row = fromApiWorkOrder(await getWorkOrder(id));
        if (cancelled) return;
        if (row.status !== 'DRAFT') {
          history.replace(detailUrl);
          return;
        }
        reset({
          mode: row.mode,
          wo_number: row.wo_number,
          wo_date: String(row.wo_date || '').slice(0, 10),
          title: row.title,
          wo_type: row.wo_type,
          priority: row.priority,
          department: row.department,
          location: row.location,
          asset: row.asset,
          description: row.description,
          direct_reason: row.direct_reason,
          rfq_id: row.rfq_id,
          rfq_number: row.rfq_number,
          quotation_number: row.quotation_number,
          other_reference: row.other_reference,
          vendor_id: row.vendor_id,
          vendor_name: row.vendor_name,
          is_open_bid: row.is_open_bid,
          submission_deadline: row.submission_deadline
            ? String(row.submission_deadline).slice(0, 16)
            : '',
          planned_start: String(row.planned_start || '').slice(0, 10),
          expected_completion: String(row.expected_completion || '').slice(0, 10),
          assigned_to: row.assigned_to,
          terms_and_conditions: row.terms_and_conditions,
          notes: row.notes,
        });
        setLines(row.items?.length ? row.items : [emptyWoLine()]);
        setVendorIds(row.invited_vendor_ids || []);
        setAttachments(row.attachments || []);
      } catch (e) {
        if (!cancelled) setSaveError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, reset, history, detailUrl]);

  const vendorOptions = vendors.map((v) => ({
    value: v.vendor_id || v.id,
    label: v.vendor_name || v.vendor_code || v.vendor_id,
  }));

  async function onSubmit(data) {
    const invitedVendors = vendors
      .filter((v) => vendorIds.includes(v.vendor_id || v.id))
      .map((v) => ({ vendor_id: v.vendor_id || v.id, vendor_name: v.vendor_name || '' }));
    const validationMsg = validateWoForm(data, lines, vendorIds);
    if (validationMsg) {
      setVendorError(/invite/i.test(validationMsg) ? validationMsg : null);
      setSaveError(validationMsg);
      toast.error(validationMsg);
      return;
    }
    const vendor = vendors.find((v) => (v.vendor_id || v.id) === data.vendor_id);
    const payload = toWoCreatePayload(
      { ...data, vendor_name: vendor?.vendor_name || data.vendor_name },
      { lines, vendorIds, attachments, invitedVendors },
    );
    const publish = submitModeRef.current === 'publish';
    const issue = submitModeRef.current === 'issue';
    submitModeRef.current = 'draft';
    setSaving(true);
    try {
      const result = await updateAndPublishWorkOrder(id, payload, { publish, issue });
      toast.success(
        publish
          ? `Work bid published. ${summarizeWoInviteNotify(result.notify)}`
          : issue ? 'Work Order issued.' : 'Draft updated.',
      );
      history.push(detailUrl);
    } catch (e) {
      setSaveError(e.message || 'Failed to save');
      toast.error(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: IPD_COLORS.primary }} />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 10 }}>
      <PageHeader
        eyebrow="Material Management"
        title="Edit Work Order"
        actions={(
          <InvButton variant="outlined" tone="teal" startIcon={<ArrowBackIcon />}
            onClick={() => history.push(detailUrl)}>
            Exit
          </InvButton>
        )}
      />
      {saveError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>{saveError}</Alert>}
      <Box component="form" id={FORM_ID} noValidate onSubmit={handleSubmit(onSubmit)}
        sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <SectionCard title="Work details">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Controller name="wo_number" control={control}
                render={({ field }) => <InvTextField {...field} label="Number" disabled />} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="mode" control={control}
                render={({ field }) => (
                  <InvSelect label="Mode" value={field.value} onChange={field.onChange} options={WO_MODES} />
                )} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="priority" control={control}
                render={({ field }) => <InvTextField {...field} label="Priority" />} />
            </Grid>
            <Grid item xs={12}>
              <Controller name="title" control={control} rules={{ required: 'Required' }}
                render={({ field }) => <InvTextField {...field} label="Work title *" error={errors.title} />} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller name="wo_type" control={control}
                render={({ field }) => (
                  <InvTextField {...field} label="Work type" placeholder={WO_TYPE_SUGGESTIONS[0]} />
                )} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller name="department" control={control}
                render={({ field }) => <InvTextField {...field} label="Department" />} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller name="location" control={control}
                render={({ field }) => <InvTextField {...field} label="Location" />} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller name="asset" control={control}
                render={({ field }) => <InvTextField {...field} label="Asset / equipment" />} />
            </Grid>
            <Grid item xs={12}>
              <Controller name="description" control={control}
                render={({ field }) => (
                  <InvTextField {...field} label="Description / scope *" multiline rows={3} />
                )} />
            </Grid>
            {mode === 'DIRECT' && (
              <Grid item xs={12}>
                <Controller name="direct_reason" control={control}
                  render={({ field }) => <InvTextField {...field} label="Reason for Direct WO *" />} />
              </Grid>
            )}
          </Grid>
        </SectionCard>

        <WoLineItemsEditor
          lines={lines}
          onChange={setLines}
          showRates={mode === 'DIRECT'}
          getPurchaseUomCode={getPurchaseUomCode}
          getBaseUomCode={getBaseUomCode}
          getPurchaseUomId={getPurchaseUomId}
          getGstRate={getGstRate}
        />

        {mode === 'DIRECT' ? (
          <SectionCard title="Vendor">
            <Controller name="vendor_id" control={control}
              render={({ field }) => (
                <InvSelect
                  label="Vendor *"
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e);
                    const hit = vendors.find((v) => (v.vendor_id || v.id) === e.target.value);
                    setValue('vendor_name', hit?.vendor_name || '');
                  }}
                  options={vendorOptions}
                />
              )} />
          </SectionCard>
        ) : (
          <>
            <SectionCard title="Bidding">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Controller name="submission_deadline" control={control}
                    render={({ field }) => (
                      <InvTextField {...field} label="Quote deadline *" type="datetime-local" />
                    )} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller name="is_open_bid" control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={(
                          <Checkbox checked={Boolean(field.value)}
                            onChange={(e) => field.onChange(e.target.checked)}
                            sx={{ color: IPD_COLORS.primary }} />
                        )}
                        label="Open bid — anyone with the URL can quote"
                      />
                    )} />
                </Grid>
              </Grid>
            </SectionCard>
            <RfqVendorSelector selectedIds={vendorIds} onChange={setVendorIds} error={vendorError} />
          </>
        )}

        <SectionCard title="Schedule and terms">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Controller name="planned_start" control={control}
                render={({ field }) => <InvTextField {...field} label="Planned start" type="date" />} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="expected_completion" control={control}
                render={({ field }) => <InvTextField {...field} label="Expected completion" type="date" />} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller name="assigned_to" control={control}
                render={({ field }) => <InvTextField {...field} label="Assigned to" />} />
            </Grid>
            <Grid item xs={12}>
              <Controller name="terms_and_conditions" control={control}
                render={({ field }) => (
                  <InvTextField {...field} label="Terms & conditions" multiline rows={3} />
                )} />
            </Grid>
          </Grid>
        </SectionCard>

        <RfqAttachmentsPanel attachments={attachments} onChange={setAttachments} />

        <Box sx={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 10,
          borderTop: `1px solid ${IPD_COLORS.border}`,
          backgroundColor: 'rgba(255,255,255,0.96)',
          px: 3, py: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1,
        }}>
          <InvButton type="submit" variant="outlined" tone="teal" loading={saving}
            onClick={() => { submitModeRef.current = 'draft'; }}>
            Save draft
          </InvButton>
          {mode === 'DIRECT' ? (
            <InvButton type="submit" tone="mint" disabled={saving}
              onClick={() => { submitModeRef.current = 'issue'; }}>Issue to vendor</InvButton>
          ) : (
            <InvButton type="submit" tone="mint" disabled={saving}
              onClick={() => { submitModeRef.current = 'publish'; }}>Publish bid</InvButton>
          )}
        </Box>
      </Box>
    </Box>
  );
}
